import { Injectable, inject } from '@angular/core';
import { LoggerService } from '@shared/services/logger.service';
import { RealtimeService } from '@shared/services/realtime.service';
import { Game, GameUnlock, GameUnlockReasonsToastrs } from '../models/game.model';
import { ToastrUtils } from '../utils/toastrs';
import {
  EnumUnlockedFeature,
  FEATURES_NEED_CALL_GET_USER_EVENTS,
  FEATURES_WITH_MODAL,
  User,
} from '../models/user.model';
import { UserRepository } from '../state/user/user.repository';
import { GamesRepository } from '../state/games/games.repository';
import { ProgressLineFacade } from '@app/progress-line/progress-line.facade';
import { Trophy } from '@app/home/trophies/trophies.model';
import { Modal, ModalType, NonPersistedModalName } from '../models/modal.model';
import { EventManager } from '../utils/event-manager';
import { GameService } from '@app/game/game.service';
import { GlobalDataRepository } from '../state/global-data/global-data.repository';
import { IncrementProgressLine } from '@app/progress-line/progress-line.model';
import { take } from 'rxjs';
import { TagsService } from './tags.service';
import { LimitedTimeEventService } from './limitedTimeEvent.service';
import { TokenService } from './token.service';
import { StreakService } from './streak.service';
import { UIRepository } from '../state/ui/ui.repository';

@Injectable({
  providedIn: 'root',
})
export class SignalREvents {
  get user(): User {
    return this.userRepo.getUser();
  }

  get gameView(): boolean {
    return !!this.globalDataRepo.getCurrentGame();
  }

  public readonly logger = inject(LoggerService);
  private readonly realtimeService = inject(RealtimeService);
  private readonly gamesRepo = inject(GamesRepository);
  private readonly toastrsUtils = inject(ToastrUtils);
  private readonly userRepo = inject(UserRepository);
  private readonly progressLineFacade = inject(ProgressLineFacade);
  private readonly eventManager = inject(EventManager);
  private readonly gameService = inject(GameService);
  private readonly globalDataRepo = inject(GlobalDataRepository);
  private readonly tagsService = inject(TagsService);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly tokenService = inject(TokenService);
  private readonly streakService = inject(StreakService);
  private readonly uiRepo = inject(UIRepository);

  public subscribeSignalREvents() {
    this.realtimeService.on('UnlockGame').subscribe({
      next: (gamesToUnlock: GameUnlock) => {
        let gameToUnlock: Game;
        gamesToUnlock.gameIds.forEach((gameId) => {
          gameToUnlock = this.gamesRepo.getGameById(gameId);

          if (gameToUnlock && !gameToUnlock.isUnlocked) {
            if (GameUnlockReasonsToastrs.includes(gamesToUnlock.reason)) {
              this.toastrsUtils.throwUnlockGameToastr(gameToUnlock);
            }

            this.gamesRepo.updateGameById(gameId, {
              isUnlocked: true,
            });

            this.uiRepo.addRecentlyUnlockedGame(gameId);
            this.tagsService.removeTagToGame(gameId, this.tagsService.getLockedTag());
          }
        });
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService.on('ProgressLineIncrement').subscribe({
      next: (incrementProgressLine: IncrementProgressLine) => {
        if (incrementProgressLine.reason === 'AchievedTrophy') {
          this.gameService.gameFinished$.pipe(take(1)).subscribe(() => {
            this.progressLineFacade.incrementUserCasino(incrementProgressLine.increment);
          });
        } else {
          this.progressLineFacade.incrementUserCasino(incrementProgressLine.increment);
        }
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService.on('WelcomeCasino').subscribe({
      next: (prizes) => {
        this.progressLineFacade.welcomePrizes.set(prizes);
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService.on('NewTrophies').subscribe({
      next: (trophies: Trophy[]) => {
        trophies.forEach((trophy) => {
          this.gamesRepo.updateGameById(trophy.gameId, {
            trophy: trophy,
          });
        });
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService.on('UnlockFeature').subscribe({
      next: (feature: EnumUnlockedFeature) => {
        if (this.user.unlockedFeatures.includes(feature)) return;

        if (FEATURES_NEED_CALL_GET_USER_EVENTS.includes(feature)) {
          this.limitedTimeEventService.requestLimitedTimeEvents();
        }

        if (FEATURES_WITH_MODAL.includes(feature)) {
          this.eventManager.openModal(
            new Modal(NonPersistedModalName.UnlockFeatureModalComponent, ModalType.View, {
              featureId: feature,
            })
          );
        } else {
          this.userRepo.updateUser({
            unlockedFeatures: [...this.user.unlockedFeatures, feature],
          });
        }
        this.eventManager.unlockedFeature(feature);
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService.on('DropToken').subscribe({
      next: () => {
        this.tokenService.refreshToken();
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService.on('NewStreak').subscribe({
      next: (newStreak: number) => {
        this.streakService.manageStreakToastr(newStreak, this.userRepo.getStreak());
        this.userRepo.updateStreak(newStreak, true);
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService.on('RestoreStreak').subscribe({
      next: (restoredStreak: number) => {
        this.userRepo.updateStreak(restoredStreak, false);
      },
      error: (error) => this.logger.error(error),
    });
  }
}
